import React, { useEffect, useRef, useState, useContext, useMemo } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { AuthContext } from "../../context/authContext";
import "./tankstelle.scss";

const items = [
    ["Tankkarten", "/tankstelle/tankkarten", "tankstelle"],
    ["Tankprotokolle", "/tankstelle/log", "tankstelle"]
];

const Tankstelle = () => {
    const ref = useRef(null);
    const moreRef = useRef(null);

    const [count, setCount] = useState(items.length);
    const [openMore, setOpenMore] = useState(false);

    const { hasPermission, isLoading } = useContext(AuthContext);
    const location = useLocation();

    // Nur erlaubte Menüpunkte
    const allowedItems = useMemo(() => {
        return items.filter(([, , resource]) =>
            hasPermission(resource, "read")
        );
    }, [hasPermission]);

    // Aktiver Menüpunkt
    const activeIndex = allowedItems.findIndex(([, path]) =>
        location.pathname === path ||
        location.pathname.startsWith(path + "/")
    );

    // Anzahl sichtbarer Menüpunkte berechnen
    useEffect(() => {
        if (!ref.current) return;

        const observer = new ResizeObserver(([entry]) => {
            const width = entry.contentRect.width;

            let used = 0;
            let visible = 0;

            for (const [label] of allowedItems) {
                const tabWidth = label.length * 8 + 40;

                if (used + tabWidth + 55 > width) {
                    break;
                }

                used += tabWidth;
                visible++;
            }

            setCount(Math.max(1, visible));
        });

        observer.observe(ref.current);

        return () => observer.disconnect();
    }, [allowedItems]);

    // Sichtbare Menüpunkte
    let visible = allowedItems.slice(0, count);

    // Aktiven Menüpunkt immer sichtbar machen
    if (activeIndex !== -1 && activeIndex >= count) {
        visible = [
            ...allowedItems.slice(0, count - 1),
            allowedItems[activeIndex]
        ];
    }

    // Versteckte Menüpunkte
    const visiblePaths = visible.map(([, path]) => path);

    const hidden = allowedItems.filter(
        ([, path]) => !visiblePaths.includes(path)
    );

    // More-Menü außerhalb schließen
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                moreRef.current &&
                !moreRef.current.contains(event.target)
            ) {
                setOpenMore(false);
            }
        };

        if (openMore) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [openMore]);

    if (isLoading) {
        return <div className="loading" />;
    }

    return (
        <div className="data">
            <Helmet>
                <title>Tankstelle</title>
            </Helmet>
            <div className="container">
                <div className="header">
                    <h1>Tankstelle</h1>
                </div>
                <div className="layout">
                    <div className="navigation" ref={ref}>
                        {visible.map(([label, path]) => (
                            <NavLink key={path} to={path}>{label}</NavLink>
                        ))}

                        {hidden.length > 0 && (
                            <div
                                className="more"
                                ref={moreRef}
                            >
                                <button type="button" onClick={() => setOpenMore(prev => !prev)}>
                                    <MoreHorizIcon />
                                </button>

                                {openMore && (
                                    <div className="more-menu">
                                        {hidden.map(([label, path]) => (
                                            <NavLink key={path} to={path} onClick={() => setOpenMore(false)}>{label}</NavLink>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="content">
                        <Outlet />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Tankstelle;